import { useEffect, useState } from "react";
import { WorkflowBoard } from "./workflow-board";
import { connectPipelineApp, resolvePipelineView } from "./app-session";
type ConnectedSession = Awaited<ReturnType<typeof connectPipelineApp>>;

export function PipelineApp() {
  const [session, setSession] = useState<ConnectedSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let connected: ConnectedSession | null = null;
    void connectPipelineApp()
      .then((value) => {
        connected = value;
        if (!active) {
          value.bridge.destroy();
          return;
        }
        applyTheme(value.context.themeContract);
        setSession(value);
      })
      .catch((cause: unknown) => {
        if (active) setError(errorMessage(cause));
      });
    return () => {
      active = false;
      connected?.bridge.destroy();
    };
  }, []);

  if (error) return <ConnectionError message={error} />;
  if (!session) return <AppLoading />;
  return (
    <WorkflowBoard
      session={session}
      initialView={
        resolvePipelineView(session.context.currentRoute) === "LEADS"
          ? "LEADS"
          : "ALL"
      }
    />
  );
}

function AppLoading() {
  return (
    <main aria-busy="true" className="center-state" role="status">
      <span className="loader" />
      <h1>Connecting Pipeline</h1>
      <p>Establishing a secure CrewFSM app session.</p>
    </main>
  );
}

function ConnectionError({ message }: { message: string }) {
  return (
    <main className="center-state" role="alert">
      <span className="error-symbol">!</span>
      <h1>Pipeline is unavailable</h1>
      <p>{message}</p>
    </main>
  );
}

function applyTheme(theme: ConnectedSession["context"]["themeContract"]) {
  const root = document.documentElement;
  const properties: Record<string, string> = {
    "--app-background": theme.colors.background,
    "--app-surface": theme.colors.surface,
    "--app-foreground": theme.colors.foreground,
    "--app-muted": theme.colors.mutedForeground,
    "--app-primary": theme.colors.primary,
    "--app-primary-foreground": theme.colors.primaryForeground,
    "--app-accent": theme.colors.accent,
    "--app-accent-foreground": theme.colors.accentForeground,
    "--app-border": theme.colors.border,
    "--app-input": theme.colors.input,
    "--app-ring": theme.colors.ring,
    "--app-success": theme.colors.success,
    "--app-warning": theme.colors.warning,
    "--app-danger": theme.colors.danger,
    "--app-info": theme.colors.info,
    "--app-radius-sm": theme.shape.radiusSmall,
    "--app-radius-md": theme.shape.radiusMedium,
    "--app-radius-lg": theme.shape.radiusLarge,
    "--app-font": theme.typography.fontFamily,
    "--app-font-weight-regular": String(theme.typography.fontWeightRegular),
    "--app-font-weight-medium": String(theme.typography.fontWeightMedium),
    "--app-font-weight-semibold": String(theme.typography.fontWeightSemibold),
    "--app-font-weight-bold": String(theme.typography.fontWeightBold),
  };
  for (const [property, value] of Object.entries(properties)) {
    root.style.setProperty(property, value);
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "CrewFSM could not complete this request.";
}
